import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { CountBand } from '../components/ui/CountBand';
import { apiList, apiPost, apiRequestGet, apiUpdate } from '../api/apiClient';
import { formatDate } from '../utils/formatters';
import { AuthContext } from '../context/AuthContext';
import { isAdmin, isDealer, isCustomer, isPropertyManager } from '../utils/roles';
import { Role } from '../types';
import { Package, PackageCheck, PackageX, Clock, RefreshCw } from 'lucide-react';
import { refreshAllCounters } from '../utils/refreshCounters';
import { deviceAccessId, eligibleUnlockDevices } from '../utils/deviceCapabilities';
import { buildVisitorPassPayload } from '../utils/visitorPassPayload';

// Delivery passes are created through the canonical PostgreSQL Visitor Pass API.
// Legacy guest_pins remain readable while historical records are migrated.

type StatusFilter = 'all' | 'outstanding' | 'completed' | 'expired' | 'revoked';

const PAGE_SIZE = 20;

const isDelivery = (g: any) =>
  String(g?.pin_type ?? g?.kind ?? '').toLowerCase() === 'delivery';

function getDeliveryStatus(d: any): 'outstanding' | 'completed' | 'expired' | 'revoked' {
  if (d.revoked || d.is_revoked) return 'revoked';
  if (d.completed_at || d.delivered_at || d.used_at) return 'completed';
  if (d.expires_at) {
    const expiry = new Date(d.expires_at);
    if (expiry < new Date()) return 'expired';
  }
  return 'outstanding';
}

export const Deliveries: React.FC = () => {
  const { user } = useContext(AuthContext);
  const role = (user?.role || 'customer') as Role;

  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [properties, setProperties] = useState<Record<number, any>>({});
  const [tenants, setTenants] = useState<Record<number, any>>({});
  const [units, setUnits] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadWarning, setLoadWarning] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);

  // Modal state
  const [selected, setSelected] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pinRevealed, setPinRevealed] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [auditEvents, setAuditEvents] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newDelivery, setNewDelivery] = useState({ property_id: '', tenant_id: '', carrier: '', courier_phone: '', notes: '', arrival_at: '', expires_at: '', access_mode: 'all' as 'all' | 'selected' | 'none', device_ids: [] as string[] });
  const [createdPass, setCreatedPass] = useState<{ directory_code?: string; pass_url: string } | null>(null);
  const [revealedPins, setRevealedPins] = useState<Record<string, string>>({});
  const [revealingPinId, setRevealingPinId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadWarning('');
    try {
      const failedSources: string[] = [];
      const partial = <T,>(label: string, work: Promise<T>, fallback: T): Promise<T> =>
        work.catch((error) => { console.error(`Deliveries ${label} failed:`, error); failedSources.push(label); return fallback; });
      const [pinData, visitorPasses, propertyData, tenantData, unitData, deviceData] = await Promise.all([
        partial('legacy delivery codes', apiList('guest_pins'), []),
        partial('secure visitor passes', apiRequestGet('/visitors/passes'), []),
        partial('properties', apiList('properties'), []),
        partial('people', apiList('tenants'), []),
        partial('units', apiList('units'), []),
        partial('devices', apiList('devices'), []),
      ]);
      if (failedSources.length) setLoadWarning(`Some delivery data could not be loaded (${failedSources.join(', ')}). The list may be incomplete. Refresh or sign in again.`);

      const propMap: Record<number, any> = {};
      (propertyData || []).forEach((p: any) => { propMap[p.id] = p; });
      setProperties(propMap);

      const tenantMap: Record<number, any> = {};
      (tenantData || []).forEach((t: any) => { tenantMap[t.id] = t; });
      setTenants(tenantMap);
      setUnits(unitData || []);
      setDevices(deviceData || []);

      // Visitor Pass is the canonical PostgreSQL source used by mobile and web.
      // Keep legacy guest_pins visible during migration, but do not omit secure
      // delivery passes generated by either mobile application.
      const passDeliveries = (Array.isArray(visitorPasses) ? visitorPasses : [])
        .filter((p: any) => String(p.pass_type).toLowerCase() === 'delivery')
        .map((p: any) => ({
          ...p,
          _source: 'visitor_pass',
          pin_type: 'delivery',
          label: p.visitor_name,
          guest_name: p.visitor_name,
          granted_by_tenant_id: p.authorized_by,
          expires_at: p.valid_until,
          created_at: p.valid_from,
          door_access_json: p.access_scope,
          delivery_notes: p.delivery_instructions,
          revoked: p.status === 'revoked',
          is_revoked: p.status === 'revoked',
        }));
      let rows = [...passDeliveries, ...(pinData || []).filter(isDelivery)];

      // Role-based scoping (same pattern as Guests.tsx)
      if (isAdmin(role)) {
        // sees all
      } else if (isDealer(role)) {
        const dealerPropertyIds = new Set(
          (propertyData || [])
            .filter((p: any) => p.integrator_id === user?.integrator_id || p.dealer_id === user?.integrator_id)
            .map((p: any) => p.id)
        );
        rows = rows.filter((g: any) => dealerPropertyIds.has(g.property_id));
      } else if (isCustomer(role)) {
        const customerPropertyIds = new Set(
          (propertyData || [])
            .filter((p: any) => p.customer_id === user?.customer_id)
            .map((p: any) => p.id)
        );
        rows = rows.filter((g: any) => customerPropertyIds.has(g.property_id));
      } else if (isPropertyManager(role)) {
        const pmPropertyIds = new Set(
          user?.property_ids || (user?.property_id ? [user.property_id] : [])
        );
        rows = rows.filter((g: any) => pmPropertyIds.has(g.property_id));
      }

      setDeliveries(rows);
    } catch (e) {
      console.error('Failed to load deliveries:', e);
      setLoadWarning('Deliveries could not be loaded. Refresh or sign in again.');
    } finally {
      setLoading(false);
    }
  }, [role, user]);

  useEffect(() => { load(); }, [load]);

  const enriched = useMemo(() => {
    return deliveries.map((d: any) => {
      const property = properties[d.property_id] || {};
      const recipientTenant = tenants[d.granted_by_tenant_id] || {};
      const unit = d.unit_number || recipientTenant.unit_number || recipientTenant.unit || 'Building';
      const status = getDeliveryStatus(d);
      return {
        ...d,
        _property_name: property.name || `Property #${d.property_id || '?'}`,
        _recipient_name: d.visitor_name || d.guest_name || d.label || [recipientTenant.first_name, recipientTenant.last_name].filter(Boolean).join(' ') || '—',
        _granted_by_name: d.granted_by_name || [recipientTenant.first_name, recipientTenant.last_name].filter(Boolean).join(' ') || 'Property Manager',
        _unit: unit || '—',
        _status: status,
        _carrier: d.delivery_carrier || d.carrier || '—',
        _notes: d.delivery_notes || d.notes || '',
        _arrival_at: d.delivery_arrival_at || d.arrival_at || null,
      };
    });
  }, [deliveries, properties, tenants]);

  const totalCount = enriched.length;
  const outstandingCount = enriched.filter(d => d._status === 'outstanding').length;
  const completedCount = enriched.filter(d => d._status === 'completed').length;
  const expiredCount = enriched.filter(d => d._status === 'expired').length;

  const filtered = useMemo(() => {
    let result = enriched;
    if (statusFilter !== 'all') result = result.filter(d => d._status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d._recipient_name.toLowerCase().includes(q) ||
        d._property_name.toLowerCase().includes(q) ||
        String(d._carrier || '').toLowerCase().includes(q) ||
        String(d._notes || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [enriched, statusFilter, search]);

  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filtered.length);
  const pageData = filtered.slice(startIndex, endIndex);

  const open = async (d: any) => {
    setSelected(d);
    setPinRevealed(false);
    setModalOpen(true);

    setAuditLoading(true);
    try {
      const events = await apiList('access_events');
      const evs = (events || [])
        .filter((e: any) => e.guest_pin_id === d.id || e.pin_code === (d.pin_code || d.pin))
        .sort((a: any, b: any) => new Date(b.created_at || b._created_at).getTime() - new Date(a.created_at || a._created_at).getTime())
        .slice(0, 50);
      setAuditEvents(evs);
    } catch {
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const close = () => {
    setModalOpen(false);
    setSelected(null);
  };

  const handleRevoke = async () => {
    if (!selected) return;
    const alreadyRevoked = selected._status === 'revoked';
    setRevoking(true);
    try {
      if (selected._source === 'visitor_pass' && alreadyRevoked) throw new Error('Revoked delivery passes cannot be reactivated; create a new pass instead.');
      const update = alreadyRevoked ? { revoked: false, is_revoked: false } : { revoked: true, is_revoked: true };
      if (selected._source === 'visitor_pass') await apiPost(`/visitors/pass/${selected.public_id}/revoke`, {});
      else await apiUpdate('guest_pins', selected.uuid || selected.id, update);
      refreshAllCounters();
      setDeliveries(prev => prev.map(g => g.id === selected.id ? { ...g, ...update } : g));
      close();
    } catch (e) {
      console.error('Failed to update delivery status:', e);
      alert(`Delivery status was not saved: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRevoking(false);
    }
  };

  const handleCreate = async () => {
    if (!newDelivery.property_id || !newDelivery.tenant_id || !newDelivery.courier_phone.trim() || !newDelivery.arrival_at) return;
    if (newDelivery.access_mode === 'selected' && newDelivery.device_ids.length === 0) {
      setCreateError('Select at least one authorized door.');
      return;
    }
    const recipient = Object.values(tenants).find((tenant: any) => String(tenant.id) === String(newDelivery.tenant_id)) || {};
    const recipientName = [recipient.first_name, recipient.last_name].filter(Boolean).join(' ') || 'Delivery';
    const unitId = recipient.unit_id ? String(recipient.unit_id) : '';
    const phone = newDelivery.courier_phone.trim();
    const validFrom = new Date(newDelivery.arrival_at);
    const validUntil = newDelivery.expires_at
      ? new Date(newDelivery.expires_at)
      : new Date(validFrom.getTime() + 24 * 60 * 60 * 1000);
    setCreateError('');
    setCreating(true);
    try {
      const result = await apiPost('/visitors/passes', buildVisitorPassPayload({
        propertyId: newDelivery.property_id,
        unitId,
        visitorName: newDelivery.carrier.trim() ? `${newDelivery.carrier.trim()} delivery for ${recipientName}` : `Delivery for ${recipientName}`,
        phone,
        passType: 'delivery',
        validFrom,
        validUntil,
        deliveryInstructions: newDelivery.notes,
        accessMode: newDelivery.access_mode,
        deviceIds: newDelivery.device_ids,
      }));
      setCreatedPass({ directory_code: result?.directory_code, pass_url: result?.pass_url });
      await load();
      refreshAllCounters();
    } catch (error: any) {
      const message = String(error?.message || error || 'Delivery pass creation failed');
      setCreateError(`Delivery pass was not created: ${message}`);
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'outstanding') return <Badge status="active">Outstanding</Badge>;
    if (status === 'completed') return <Badge status="active">Completed</Badge>;
    if (status === 'expired') return <Badge status="expired">Expired</Badge>;
    if (status === 'revoked') return <Badge status="suspended">Revoked</Badge>;
    return <Badge>{status}</Badge>;
  };

  const columns = [
    {
      key: 'recipient',
      header: 'Recipient',
      render: (d: any) => (
        <button onClick={() => open(d)} className="font-medium text-brand-600 hover:text-brand-800 text-left">
          {d._recipient_name}
        </button>
      ),
    },
    {
      key: 'carrier',
      header: 'Carrier / Notes',
      render: (d: any) => (
        <div className="text-sm">
          <div className="text-gray-700">{d._carrier}</div>
          {d._notes && <div className="text-gray-400 text-xs truncate max-w-xs" title={d._notes}>{d._notes}</div>}
        </div>
      ),
    },
    {
      key: 'property',
      header: 'Property',
      render: (d: any) => <span className="font-medium text-gray-900">{d._property_name}</span>,
    },
    {
      key: 'granted_by',
      header: 'Granted By',
      render: (d: any) => d._granted_by_name,
    },
    {
      key: 'unit',
      header: 'Unit',
      render: (d: any) => d._unit !== '—' ? `#${d._unit}` : '—',
    },
    {
      key: 'method',
      header: 'Method',
      render: () => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
          PIN only
        </span>
      ),
    },
    {
      key: 'pin',
      header: 'PIN',
      render: (d: any) => {
        const id = String(d.id);
        const revealed = revealedPins[id];
        return <div className="inline-flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
          <span className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{revealed || '••••••'}</span>
          <button type="button" className="row-open" disabled={revealingPinId === id} onClick={async () => {
            if (revealed) setRevealedPins((current) => { const next = { ...current }; delete next[id]; return next; });
            else {
              let pin = d.pin_code || d.pin;
              if (!pin && d._source === 'visitor_pass') {
                setRevealingPinId(id);
                try {
                  const result = await apiRequestGet(`/visitors/passes/${encodeURIComponent(id)}/pin`);
                  pin = result?.pin_code;
                } catch (error: any) {
                  alert(`PIN could not be loaded: ${error?.message || error}`);
                } finally {
                  setRevealingPinId(null);
                }
              }
              if (pin) {
                setRevealedPins((current) => ({ ...current, [id]: String(pin) }));
                window.setTimeout(() => setRevealedPins((current) => { const next = { ...current }; delete next[id]; return next; }), 10000);
              }
            }
          }}>{revealingPinId === id ? 'Loading…' : revealed ? 'Hide' : 'View'}</button>
        </div>;
      },
    },
    {
      key: 'arrival',
      header: 'Expected Arrival',
      render: (d: any) => d._arrival_at ? formatDate(d._arrival_at) : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'expires',
      header: 'Valid Until',
      render: (d: any) => d.expires_at ? formatDate(d.expires_at) : <span className="text-gray-400 text-xs">No Expiry</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (d: any) => getStatusBadge(d._status),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (d: any) => (
        <button
          onClick={() => open(d)}
          className="row-open"
        >
          Open <span aria-hidden="true">→</span>
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="shadow-list-page prototype-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deliveries</h1>
          <p className="text-gray-500 mt-1">Delivery PIN grants — PIN-only, back-of-house (not shown in directory or simring)</p>
        </div>
      </div>
      {loadWarning && <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">{loadWarning}</div>}

      <div className="prototype-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search deliveries..." />
        <label>
          <small>Status</small>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="all">All</option>
            <option value="outstanding">Outstanding</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </label>
        <button className="prototype-refresh" onClick={load} aria-label="Refresh deliveries">
          <RefreshCw /> <span>Refresh</span>
        </button>
        <Button onClick={() => { setCreateError(''); setShowCreate(true); }}>Add Delivery</Button>
      </div>

      <CountBand value={filtered.length} detail="Deliveries in the current accessible portfolio" />

      {/* Summary cards */}
      <div className="legacy-summary-cards grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Deliveries</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
            <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-brand-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Outstanding</p>
              <p className="text-2xl font-bold text-blue-600">{outstandingCount}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{completedCount}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <PackageCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Expired</p>
              <p className="text-2xl font-bold text-yellow-600">{expiredCount}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <PackageX className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-2">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">
              No deliveries found.
            </div>
          ) : pageData.map((d: any) => (
            <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-3" onClick={() => open(d)}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{d._recipient_name}</p>
                  <p className="text-xs text-gray-500 truncate">📦 {d._carrier} · {d._property_name}</p>
                </div>
                {getStatusBadge(d._status)}
              </div>
              <div className="mobile-card-actions">
                <span className="mr-auto text-xs text-gray-600">Granted by {d._granted_by_name} · Unit {d._unit}</span>
                <button onClick={(e) => { e.stopPropagation(); open(d); }} className="row-open">View <span aria-hidden="true">→</span></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop table */}
      <Card padding={false} className="hidden md:block">
        <div className="legacy-table-controls p-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[240px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by recipient, carrier, property..."
              />
            </div>
            <div className="flex items-center gap-2">
              {(['all', 'outstanding', 'completed', 'expired', 'revoked'] as StatusFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-2 text-sm rounded-lg capitalize ${
                    statusFilter === f ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <Table columns={columns} data={pageData} emptyMessage="No deliveries found" />

        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={setPage}
        />
      </Card>

      <Modal open={showCreate} onClose={() => { setCreateError(''); setCreatedPass(null); setShowCreate(false); }} title="Add Delivery">
        <div className="space-y-4">
          {createdPass ? <div className="space-y-4" role="status">
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4"><p className="font-semibold text-emerald-900">Delivery pass created</p><p className="mt-1 text-sm text-emerald-800">Share the secure link. The recipient can verify their phone and use the access allowed by this property.</p></div>
            {createdPass.directory_code && <div><p className="text-xs font-bold uppercase tracking-wider text-gray-500">Directory code</p><p className="font-mono text-2xl font-bold tracking-widest text-gray-900">{createdPass.directory_code}</p></div>}
            <a className="block break-all text-sm font-semibold text-emerald-700 underline" href={createdPass.pass_url} target="_blank" rel="noreferrer">Open delivery pass</a>
            <div className="flex flex-wrap gap-2"><Button onClick={() => navigator.clipboard.writeText(createdPass.pass_url)}>Copy secure link</Button><Button variant="secondary" onClick={() => { setCreatedPass(null); setNewDelivery({ property_id: '', tenant_id: '', carrier: '', courier_phone: '', notes: '', arrival_at: '', expires_at: '', access_mode: 'all', device_ids: [] }); }}>Add another</Button><Button variant="secondary" onClick={() => setShowCreate(false)}>Done</Button></div>
          </div> : <>
          <label className="block text-sm font-medium text-gray-700">Property<select className="mt-1 w-full border border-gray-300 px-3 py-2" value={newDelivery.property_id} onChange={(e) => setNewDelivery((value) => ({ ...value, property_id: e.target.value, tenant_id: '', device_ids: [] }))}><option value="">Select a property</option>{Object.values(properties).sort((a: any, b: any) => String(a.name).localeCompare(String(b.name))).map((property: any) => <option key={property.id} value={property.id}>{property.name}</option>)}</select></label>
          <label className="block text-sm font-medium text-gray-700">Unit / recipient<select className="mt-1 w-full border border-gray-300 px-3 py-2" value={newDelivery.tenant_id} onChange={(e) => setNewDelivery((value) => ({ ...value, tenant_id: e.target.value }))}><option value="">Select a unit and resident</option>{Object.values(tenants).filter((tenant: any) => (!newDelivery.property_id || String(tenant.property_id) === String(newDelivery.property_id)) && tenant.unit_id).sort((a: any, b: any) => String(a.unit_number || '').localeCompare(String(b.unit_number || ''), undefined, { numeric: true })).map((tenant: any) => { const unit = units.find((item: any) => String(item.id) === String(tenant.unit_id)); const property = properties[Number(tenant.property_id)]; return <option key={tenant.id} value={tenant.id}>{property?.name || 'Property'} · Unit {unit?.unit_number || unit?.display_name || tenant.unit_number || 'Unnumbered'} · {[tenant.first_name, tenant.last_name].filter(Boolean).join(' ') || 'Resident'}</option>; })}</select></label>
          <label className="block text-sm font-medium text-gray-700">Carrier<input className="mt-1 w-full border border-gray-300 px-3 py-2" value={newDelivery.carrier} onChange={(e) => setNewDelivery((value) => ({ ...value, carrier: e.target.value }))} placeholder="UPS, FedEx…" /></label>
          <label className="block text-sm font-medium text-gray-700">Delivery contact phone<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="tel" autoComplete="tel" value={newDelivery.courier_phone} onChange={(e) => setNewDelivery((value) => ({ ...value, courier_phone: e.target.value }))} placeholder="Phone used to verify the secure pass" /></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><label className="block text-sm font-medium text-gray-700">Expected arrival<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="datetime-local" value={newDelivery.arrival_at} onChange={(e) => setNewDelivery((value) => ({ ...value, arrival_at: e.target.value }))} /></label><label className="block text-sm font-medium text-gray-700">Access ends<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="datetime-local" value={newDelivery.expires_at} onChange={(e) => setNewDelivery((value) => ({ ...value, expires_at: e.target.value }))} /></label></div>
          <label className="block text-sm font-medium text-gray-700">Notes<textarea className="mt-1 w-full border border-gray-300 px-3 py-2" rows={3} value={newDelivery.notes} onChange={(e) => setNewDelivery((value) => ({ ...value, notes: e.target.value }))} /></label>
          <fieldset className="border border-gray-300 p-3"><legend className="px-1 text-sm font-semibold text-gray-800">Door access</legend>
            <div className="flex flex-wrap gap-2">{(['all', 'selected', 'none'] as const).map((mode) => <label key={mode} className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-2 text-sm"><input type="radio" name="global-delivery-access-mode" checked={newDelivery.access_mode === mode} onChange={() => setNewDelivery((value) => ({ ...value, access_mode: mode, device_ids: mode === 'selected' ? value.device_ids : [] }))} />{mode === 'all' ? 'All eligible doors' : mode === 'selected' ? 'Selected doors' : 'No door access'}</label>)}</div>
            {newDelivery.access_mode === 'selected' && <div className="mt-3 space-y-2">{eligibleUnlockDevices(devices.filter((device: any) => String(device.property_id) === String(newDelivery.property_id))).map((device: any) => { const deviceId = deviceAccessId(device); return <label key={deviceId} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newDelivery.device_ids.includes(deviceId)} onChange={() => setNewDelivery((value) => ({ ...value, device_ids: value.device_ids.includes(deviceId) ? value.device_ids.filter((id) => id !== deviceId) : [...value.device_ids, deviceId] }))} />{device.name || device.device_name || 'Unnamed door'}</label>; })}</div>}
            <p className="mt-2 text-xs text-gray-600">{newDelivery.access_mode === 'all' ? 'All current and future eligible doors at this property.' : newDelivery.access_mode === 'none' ? 'No door unlock access.' : `${newDelivery.device_ids.length} selected door${newDelivery.device_ids.length === 1 ? '' : 's'}.`}</p>
          </fieldset>
          {createError && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{createError}</div>}
          <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => { setCreateError(''); setShowCreate(false); }}>Cancel</Button><Button disabled={creating || !newDelivery.property_id || !newDelivery.tenant_id || !newDelivery.courier_phone.trim() || !newDelivery.arrival_at || (newDelivery.access_mode === 'selected' && newDelivery.device_ids.length === 0)} onClick={handleCreate}>{creating ? 'Creating…' : 'Create secure delivery pass'}</Button></div>
          </>}
        </div>
      </Modal>

      {/* Detail modal */}
      {selected && (
        <Modal open={modalOpen} onClose={close} title={`Delivery — ${selected._recipient_name}`} size="lg">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Recipient</p>
                <p className="font-medium text-gray-900">{selected._recipient_name}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Property / Unit</p>
                <p className="font-medium text-gray-900">{selected._property_name} · {selected._unit !== '—' ? `Unit ${selected._unit}` : 'Building'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Carrier</p>
                <p className="font-medium text-gray-900">{selected._carrier}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Method</p>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  Secure visitor pass
                </span>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Expected Arrival</p>
                <p className="font-medium text-gray-900">{selected._arrival_at ? formatDate(selected._arrival_at) : '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Valid Until</p>
                <p className="font-medium text-gray-900">{selected.expires_at ? formatDate(selected.expires_at) : 'No Expiry'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">PIN Code</p>
                <div className="flex items-center gap-2">
                  <p className="font-mono font-bold text-gray-900">
                    {pinRevealed ? (selected.pin_code || selected.pin || '—') : '••••'}
                  </p>
                  <button
                    onClick={() => setPinRevealed(!pinRevealed)}
                    className="text-xs text-brand-600 hover:underline"
                  >
                    {pinRevealed ? 'Hide' : 'View PIN'}
                  </button>
                  {pinRevealed && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(selected.pin_code || selected.pin || ''); }}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Copy
                    </button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Status</p>
                {getStatusBadge(selected._status)}
              </div>
            </div>

            {selected._notes && (
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Delivery Notes</p>
                <p className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded p-2">{selected._notes}</p>
              </div>
            )}

            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Access Audit Trail</p>
              {auditLoading ? (
                <p className="text-xs text-gray-400">Loading events...</p>
              ) : auditEvents.length === 0 ? (
                <p className="text-xs text-gray-400">No access events recorded</p>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {auditEvents.map((event: any, idx: number) => (
                    <div key={idx} className="px-3 py-2 text-xs flex items-center justify-between">
                      <div>
                        <span className={`font-medium ${event.event_type === 'unlock' || event.action === 'unlock' ? 'text-green-600' : 'text-gray-700'}`}>
                          {event.event_type || event.action || 'Access'}
                        </span>
                        {event.door_name && <span className="text-gray-400 ml-2">— {event.door_name}</span>}
                      </div>
                      <span className="text-gray-400">
                        {event.created_at ? new Date(event.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <button
                onClick={handleRevoke}
                disabled={revoking || (selected._source === 'visitor_pass' && selected._status === 'revoked')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selected._status === 'revoked'
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {revoking ? 'Saving...' : selected._status === 'revoked' ? 'Reactivate Delivery' : 'Revoke Delivery'}
              </button>

              <button
                onClick={close}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
